import React, { useState, useEffect, useRef, Suspense } from "react";
import {
  Box,
  Heading,
  Text,
  Button,
  Divider,
  Image,
  Center,
  VStack,
  Input,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useToast,
} from "@chakra-ui/react";
import { io } from "socket.io-client";
import Icon from "@mdi/react";
import { mdiCamera } from "@mdi/js";
import { IFrameFooterWithLogo } from "components/Footer";
import { SOCKET_URL } from "./utils";
import { Loading } from "components/KYC2/graphics";
import { compressImgFileIfLargerThanTarget, useAnalytics, getDimensionsFromImgFile } from "utils";

/**
 * ImageCaptureComponent
 * @name ImageCaptureComponent
 * @param {string} refId
 * @param {string} buttonText
 * @param {SVGElement} PlaceHolderImage
 * @param {string} imageAlt
 * @param {string} roomid
 * @param {Socket} socket
 * @returns JSX.Element
 * @description Used within the MobileUploadComponent to handle capturing a single image.
 * It's expected that there will be one or more of these depending on how many images need to be capture.
 * The reference ID is what is used to differentiate which image has been uploaded.
 */
const ImageCaptureComponent = ({
  refId,
  buttonText,
  PlaceHolderImage,
  imageAlt,
  socket,
  roomid,
}) => {
  const toast = useToast();
  const analytics = useAnalytics();
  const [img, setImg] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const fileInputRef = useRef();

  const readFile = (file) => {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.onloadend = function (event) {
        const data = event?.target?.result || null;
        resolve(data);
      };
      fileReader.onerror = reject;
      fileReader.readAsDataURL(file);
    });
  };

  const handleOnChange = async (event) => {
    try {
      event.stopPropagation();
      event.preventDefault();
      setIsLoading(true);
      const fileInputEl = event.target;
      const f = event.target.files[0];
      if (f.type.includes("image/")) {
        const file = await compressImgFileIfLargerThanTarget(event.target.files[0]);
        const data = await readFile(file);
        const dim = await getDimensionsFromImgFile(file);
        if (dim.width >= 450 && dim.height >= 600) {
          setImg(data);
          analytics.track("CaptureOnMobile_Image_Selected", { refId, roomid });
          socket.emit("client-image-upload", refId, data);
        } else {
          toast({
            title: "Image is too small",
            description: "Please select an image larger than 450 x 600",
            status: "error",
            isClosable: true,
          });
          if (fileInputEl.value) {
            fileInputEl.value = ""; // clear current selection
          }
        }
      } else {
        /**
         * This will only fire if the user changes which file they've selected
         * as <input type="file" /> is fully user controlled and the "change" event
         * won't fire if the file the user selected hasn't changed.
         */
        toast({
          title: "Unsupported file type",
          description: "Please select a jpg or a png image file.",
          status: "error",
          isClosable: true,
        });
      }
      setIsLoading(false);
    } catch (err) {
      toast({
        title: "Error",
        description:
          err.message || "There was an issue with your file selection.  Please try again.",
        status: "error",
        isClosable: true,
      });
    }
  };

  return (
    <Box w="100%" data-testid="image-capture-component">
      {img ? (
        <Image width="100%" src={img} alt={imageAlt} pb={2} data-testid="image-preview" />
      ) : (
        <Suspense fallback={<Loading />}>
          <PlaceHolderImage />
        </Suspense>
      )}
      <Input
        type="file"
        accept="image/png,image/jpeg"
        ref={fileInputRef}
        onChange={handleOnChange}
        style={{ display: "none" }}
        data-testid="file-input-element"
      />
      <Button
        variant="outline"
        leftIcon={<Icon path={mdiCamera} size={"1em"} />}
        onClick={() => {
          if (fileInputRef && fileInputRef.current) {
            fileInputRef.current.click();
          }
        }}
        borderColor="rgba(27, 34, 40, 0.18)"
        width="full"
        isLoading={isLoading}
        mt={4}
      >
        {buttonText}
      </Button>
    </Box>
  );
};

/**
 * MobileUploadComponent
 * @name MobileUploadComponent
 * @param {string} roomid - id of socket room to send uploads to
 * @param {string} title
 * @param {string} description
 * @param {MobileUploadItem[]} items
 * @returns JSX.Element
 * @description Component to handle capturing images and passing along the data to another browser through a websocket connection
 */
const MobileUploadComponent = ({ roomid, title, description, items }) => {
  const [socket, setSocket] = useState(null);
  const isValid = roomid && title && description && Array.isArray(items) && items.length > 0;

  useEffect(() => {
    if (!socket) {
      // console.log("no socket, will create"); // leaving some console.logs commented out here since we can't use debug without being sessionCtx, and here we don't have a logged in user
      setSocket(io(SOCKET_URL));
    } else {
      // Join a channel
      // console.log("has socket, will connect to room:", roomid);
      socket.emit("join", roomid);

      // client-side
      socket.on("connect", () => {
        // console.log("==> in on.connect", socket.id); // x8WIv7-mJelg7on_ALbx
      });

      // eslint-disable-next-line no-unused-vars
      socket.on("connect_error", (err) => {
        // the reason of the error, for example "xhr poll error"
        // console.error("[socket: connect_error] msg: ", err.message);
        // some additional description, for example the status code of the initial HTTP response
        // console.error("[socket: connect_error] description: ", err.description);
        // some additional context, for example the XMLHttpRequest object
        // console.log(err.context);
      });

      socket.on("disconnect", () => {
        // console.log("==> in on.disconnect"); // undefined
      });
    }
  }, [socket, roomid]);

  return isValid ? (
    <Box>
      <Box px={4} pb={6}>
        <Heading mb={2}>{title}</Heading>
        <Text fontSize="sm" color="secondaryText">
          {description}
        </Text>
      </Box>
      <Divider />
      <Center>
        <VStack w="100%" px={4} py={6} spacing={8}>
          {items.map((c) => (
            <ImageCaptureComponent roomid={roomid} key={c.refId} socket={socket} {...c} />
          ))}
        </VStack>
      </Center>
      <IFrameFooterWithLogo />
    </Box>
  ) : (
    <Alert status="error">
      <AlertIcon />
      <AlertTitle>Error:</AlertTitle>
      <AlertDescription>Unknown document type requested.</AlertDescription>
    </Alert>
  );
};

export default MobileUploadComponent;
